/**
 * Shared device viewports for responsive work and automated layout checks.
 *
 * The iPhone Duo sizes are CSS-pixel viewports taken from public device
 * guides. If Apple publishes different official viewports, update them here;
 * application code, Playwright projects and browser harnesses import these
 * values and must not hard-code the numbers.
 *
 * This module uses only erasable TypeScript syntax so Node can import it
 * directly from the `tests/*.browser.mjs` harnesses.
 */

export type DuoPostureName =
  | 'duo-folded'
  | 'duo-folded-sideways'
  | 'duo-unfolded-wide'
  | 'duo-unfolded-tall'
  | 'duo-half-open';

export interface DeviceViewport {
  /** Stable kebab-case identifier used for project names and screenshot files. */
  readonly name: string;
  /** Human-readable posture description. */
  readonly label: string;
  readonly width: number;
  readonly height: number;
  /** True when the Larger Text / Display Zoom transform has been applied. */
  readonly largerText: boolean;
  /** Root font size as a percentage of the browser default (100 = unchanged). */
  readonly rootFontPercent: number;
}

/** Below this width the app uses the phone layout family (matches `md`). */
export const PHONE_BREAKPOINT_PX = 768;

/** Media query matching the phone layout family (width below the breakpoint). */
export const PHONE_MEDIA_QUERY = `(max-width: ${PHONE_BREAKPOINT_PX - 1}px)`;

/** Media query matching the desktop layout family (inline sidebar). */
export const DESKTOP_MEDIA_QUERY = `(min-width: ${PHONE_BREAKPOINT_PX}px)`;

/**
 * Two-pane screens keep both panes side by side only when each pane can be at
 * least this wide; otherwise the secondary pane collapses behind a toggle.
 */
export const MIN_SPLIT_PANE_PX = 320;

/** Gap between the two panes of a split view, in CSS pixels. */
export const SPLIT_PANE_GAP_PX = 16;

/** Overlays (dialogs, drawers) are capped at this width in the phone layout family. */
export const PHONE_OVERLAY_MAX_WIDTH_PX = 560;

/** Viewports no taller than this are "short" (folded sideways, half-open): overlay actions stay pinned. */
export const SHORT_VIEWPORT_MAX_HEIGHT_PX = 560;

/** True when a container of this width can show both split-view panes side by side. */
export function splitPanesFit(containerWidth: number, gap: number = SPLIT_PANE_GAP_PX): boolean {
  return containerWidth >= MIN_SPLIT_PANE_PX * 2 + gap;
}

/** Minimum comfortable touch target in CSS pixels (WCAG 2.5.5 / Apple HIG). */
export const MIN_TAP_TARGET_PX = 44;

/** Larger Text / Display Zoom: ~15% smaller effective viewport and a 120% root font. */
export const LARGER_TEXT = { viewportScale: 0.85, rootFontPercent: 120 } as const;

/** Device pixel ratio used when emulating the Duo in automated checks. */
export const DUO_DEVICE_SCALE_FACTOR = 2;

/** iOS Safari user agent used for touch emulation in automated checks. */
export const IOS_SAFARI_USER_AGENT =
  'Mozilla/5.0 (iPhone; CPU iPhone OS 18_0 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/18.0 Mobile/15E148 Safari/604.1';

function viewport(name: string, label: string, width: number, height: number): DeviceViewport {
  return Object.freeze({ name, label, width, height, largerText: false, rootFontPercent: 100 });
}

export const DUO_FOLDED = viewport('duo-folded', 'Folded, upright', 466, 678);
export const DUO_FOLDED_SIDEWAYS = viewport('duo-folded-sideways', 'Folded, sideways', 678, 466);
export const DUO_UNFOLDED_WIDE = viewport('duo-unfolded-wide', 'Unfolded, held wide', 890, 626);
export const DUO_UNFOLDED_TALL = viewport('duo-unfolded-tall', 'Unfolded, held tall', 626, 890);
/** Half-open (laptop posture): content area is roughly the top half of the unfolded-tall screen. */
export const DUO_HALF_OPEN = viewport('duo-half-open', 'Half-open (top half)', 626, 445);

/** Existing reference sizes used by the browser harnesses. */
export const REFERENCE_DESKTOP = viewport('desktop', 'Desktop reference', 1440, 1000);
export const REFERENCE_PHONE = viewport('phone', 'Phone reference', 390, 844);

/** Apply the Larger Text / Display Zoom transform to any viewport. */
export function largerText(base: DeviceViewport): DeviceViewport {
  if (base.largerText) return base;
  return Object.freeze({
    name: `${base.name}-larger-text`,
    label: `${base.label}, Larger Text`,
    width: Math.round(base.width * LARGER_TEXT.viewportScale),
    height: Math.round(base.height * LARGER_TEXT.viewportScale),
    largerText: true,
    rootFontPercent: LARGER_TEXT.rootFontPercent
  });
}

/** The five physical iPhone Duo postures. */
export const DUO_POSTURES: readonly DeviceViewport[] = Object.freeze([
  DUO_FOLDED,
  DUO_FOLDED_SIDEWAYS,
  DUO_UNFOLDED_WIDE,
  DUO_UNFOLDED_TALL,
  DUO_HALF_OPEN
]);

/** Full Duo matrix: every posture plus its Larger Text variant. */
export const DUO_MATRIX: readonly DeviceViewport[] = Object.freeze([
  ...DUO_POSTURES,
  ...DUO_POSTURES.map(largerText)
]);

/**
 * Select matrix entries from a comma-separated filter (for example the
 * `DUO_POSTURES` environment variable). Each token matches a viewport name
 * exactly; a posture name also selects its Larger Text variant. An empty
 * filter returns the whole matrix.
 */
export function selectDuoMatrix(filter: string | undefined, matrix: readonly DeviceViewport[] = DUO_MATRIX): DeviceViewport[] {
  const tokens = (filter ?? '').split(',').map((token) => token.trim()).filter(Boolean);
  if (tokens.length === 0) return [...matrix];
  const selected = matrix.filter((entry) => tokens.some((token) => entry.name === token || entry.name === `${token}-larger-text`));
  if (selected.length === 0) {
    throw new Error(`No Duo viewport matches "${filter}". Known names: ${matrix.map((entry) => entry.name).join(', ')}`);
  }
  return selected;
}

/** True when a viewport width falls into the phone layout family. */
export function isPhoneWidth(width: number): boolean {
  return width < PHONE_BREAKPOINT_PX;
}
